import _ from 'lodash';
import {castToSisTypePrimitive} from 'util/SisType';
import {CustomFieldTO, SisType} from "generated/JavaTO"

import * as Backbone from "backbone";
import {getter, setter} from "util/BackboneCompatUtil";
import {InputType} from "module/CustomField/util/CFInputType";
import {getSisDateAsISO, getSisDateAsNumber, getSisDurationAsISO, getSisDurationAsNumber, getSisTimeAsISO, getSisTimeAsNumber, SisDurationOptionsType, SisTimeOptionsType} from 'util/SisDateTime';
import {applySisKeyFieldRuleset, setFixedStringAt, substrTypeaware} from 'util/StringUtil';
import {CFGetOptions, CFMember, CFModel, CFSetOptions, CFValue} from 'module/CustomField/type/CFType';
import SisDuration from 'util/SisDuration'

const ensureMaxLength = (cfDef: CustomFieldTO, val: CFValue) => {
	if (CF.isCFNamespace(cfDef)) return val;
	switch (cfDef.type) {
		//number types right aligned
		case SisType.Duration:
		case SisType.Duration2:
		case SisType.Date:
		case SisType.Time:
		case SisType.Int:
		case SisType.Number:
		case SisType.Decimal:
			// cut away left part
			let valLen = (val + '').length;
			let start = valLen - cfDef.length;
			if (start < 0) start = 0;
			return substrTypeaware(val, start, valLen);
		//string types left aligned
		default:
			// cut away right part
			return substrTypeaware(val, 0, cfDef.length);
	}
};

export const CF = {
	isCF(cfDef: CustomFieldTO) {
		return cfDef && cfDef.hasOwnProperty('id') && cfDef.hasOwnProperty('dstField');
	},

	isRawCF(cfDef: CustomFieldTO) {
		return (cfDef.input === InputType.Verbose || cfDef.input === InputType.Raw || !CF.isSingleValueCF(cfDef));
	},

	isSingleValueCF(cfDef: CustomFieldTO) {
		return cfDef.type !== SisType.Blob && cfDef.type !== SisType.GPS;
	},

	isReadOnlyInput(cfDef: CustomFieldTO) {
		// todo use insertable/updatable
		return cfDef.input && (cfDef.input + '')[2] === 'L';
	},

	isCFNamespace(cfDef: CustomFieldTO) {
		return _.startsWith(cfDef.dstField, 'cf.')
	},
	isGroupedCF(cfDef: CustomFieldTO) {
		return cfDef && cfDef.hasOwnProperty('cfGroupId') && cfDef.cfGroupId !== null;
	},
	getCFNamespaceField(cfDef: CustomFieldTO) {
		if (!CF.isCFNamespace(cfDef)) throw new Error("invalid call to getCFNamespaceField");
		return cfDef.dstField.substr(3);
	},
	getCFAttrFromCFAttrs(cfDef: CustomFieldTO, searchKey: string = '') {
		return cfDef && cfDef?.cfAttrs?.find(cfa => cfa.key === searchKey)
	},
	/**
	 * @param {CustomFieldTO} cfDef
	 * @param {Backbone.Model} model
	 * @param {{}} [options]
	 * @param [options.pos] exact position in cf Namespace
	 * @param {boolean} [options.forView = false] set to true if you want to display the value in a text field with .val().
	 * @returns {*} new value for input field. apply this value to the field because it is the correct representation in the model
	 */
	getValueFrom<T extends CFModel>(cfDef: CustomFieldTO, model: T | null | undefined, options?: CFGetOptions): CFValue | CFMember | CFMember[] {
		let {dstField} = cfDef;
		if (CF.isCFNamespace(cfDef)) {
			dstField = CF.getCFNamespaceField(cfDef);
			const cf = getter(model, 'cf');
			let ret: CFValue | CFMember | CFMember[];
			const isSingleValueCF = CF.isSingleValueCF(cfDef);
			const singleValueGetter = typeof options?.pos === 'number';
			if (singleValueGetter) {
				ret = _.find(cf, {dstField, pos: options.pos}) as CFMember
			} else {
				ret = _.filter(cf, {dstField}) as CFMember[]
			}
			if (!ret) return null;
			ret = _.cloneDeep(ret);
			if (CF.isRawCF(cfDef)) {
				return ret;
			} else if (singleValueGetter) {
				return isSingleValueCF ? CF.doBeforeReturnFromGetter(cfDef, (ret as CFMember).data, options) : ret;
			} else {
				if (isSingleValueCF) {
					return (ret as CFMember[]).map((r: CFMember) => {
						r.data = CF.doBeforeReturnFromGetter(cfDef, r.data, options);
						if (r.data == null || _.isNaN(r.data)) r = _.omit(r, 'data');
						return r;
					});
				} else {
					return ret;
				}
			}
		} else {
			let rawValue = getter(model, dstField);
			if (CF.isRawCF(cfDef)) return rawValue;
			let val = substrTypeaware(rawValue, cfDef.startIndex, cfDef.length);
			return CF.doBeforeReturnFromGetter(cfDef, val, options);
		}
	},

	doBeforeReturnFromGetter(cfDef: CustomFieldTO, val: CFValue, options?: CFGetOptions) {
		val = CF.applyPrimitiveType(cfDef, val); // apply type for number field validations from text fields
		// noinspection EqualityComparisonWithCoercionJS
		if (CF.isNull(cfDef, val)) val = null;
		if (options?.forView) val = CF.transformForView(cfDef, val);
		if (val === undefined) val = null;
		return val;
	},

	/**
	 * @param {CustomFieldTO} cfDef
	 * @param {Object|Backbone.Model} model
	 * @param {*} val value from input field. Can also be valueyarndev object with 'pos' attribute (i.e. {data:' ', pos:0})
	 * @param {Object} [options] backbone set options
	 * @param {Integer} [options.pos] optional usage of pos attribute for 'cf.*' attributes, overrides 'pos'-attribute from 'val'-parameter
	 * @param {boolean} [options.userInput = false] set to true if you want apply appropriate adapt methods. requires refresh view value after using this setter.
	 * @returns {*} new value for input field. apply this value to the field because it is the correct representation in the model
	 */
	setValueTo<T extends object>(cfDef: CustomFieldTO, model: T, val: CFValue | CFMember, options?: CFSetOptions) {
		let {dstField} = cfDef;
		const attr = (model instanceof Backbone.Model) ? model.attributes : model;
		if (CF.isCFNamespace(cfDef)) {
			dstField = CF.getCFNamespaceField(cfDef);
			let objVal = (_.isPlainObject(val) ? _.cloneDeep(val) : {data: val, pos: undefined, dstField}) as Partial<CFMember>
			objVal.dstField = dstField;
			if (options && typeof options.pos === 'number') {
				objVal.pos = options.pos;
			}
			if (typeof objVal.pos !== 'number' || objVal.pos < 0 || objVal.pos > 999999999) {
				throw new Error("missing pos attribute in CF.setValueTo");
			}
			objVal.data = CF.mergeAttr(cfDef, null, objVal.data, options);
			const cf = _.filter(attr.cf, cfMember => cfMember.pos !== objVal.pos || cfMember.dstField !== objVal.dstField);
			if (objVal.data == null || _.isNaN(objVal.data)) objVal = _.omit(objVal, 'data');
			else objVal.data += '' // AttachmentTO.data is always string
			setter(model, 'cf', cf.concat(objVal).sort(CFMemberComparator), options);
		} else {
			if (CF.isRawCF(cfDef)) {
				setter(model, dstField, val, options);
			} else {
				const dst = _.get(attr, dstField);
				const newVal = CF.mergeAttr(cfDef, dst, (val as CFValue), options);
				setter(model, dstField, newVal, options);
			}
		}
	},

	adaptBeforeSet(cfDef: CustomFieldTO, val: CFValue, options?: CFSetOptions) {
		val = CF.parseValue(cfDef, val, options)
		val = ensureMaxLength(cfDef, val)
		val = CF.applyPrimitiveType(cfDef, val)
		if (cfDef.nullValue !== null && CF.isNull(cfDef, val)) {
			//for H, T type fields: do not display value if 0 =>=> set nullValue in cfdef
			val = cfDef.nullValue
			val = CF.applyPrimitiveType(cfDef, val)
		}
		if (cfDef.defaultValue !== null && CF.isNull(cfDef, val)) {
			val = cfDef.defaultValue
			val = CF.applyPrimitiveType(cfDef, val)
		}
		return val
	},
	/**
	 * generates model-value(combined cf values) from current value and single field value
	 * this value can be set as new one
	 * @param {boolean} [options.userInput = false] set to true if you want apply appropriate adapt methods. requires refresh view value after using this setter.
	 */
	mergeAttr(cfDef: CustomFieldTO, currAttr: CFValue, val: CFValue, options?: CFSetOptions) {
		if (CF.isReadOnlyInput(cfDef)) return currAttr;
		if (CF.isRawCF(cfDef)) return val;

		val = CF.adaptBeforeSet(cfDef, val, options)

		if (cfDef.startIndex !== 0 || (currAttr && (currAttr + '').length > cfDef.length)) {
			//either is not first value in field
			//or
			//there are more fields already set
			val = setFixedStringAt(currAttr, val, cfDef.startIndex, cfDef.length, {nullValueRepresentation: cfDef.nullValue});
			val = _.trimEnd(val);
			if (!val) val = cfDef.nullValue;
		} //else if(val === null)
		return val;
	},

	isNull(cfDef: CustomFieldTO, val: CFValue) {
		switch (cfDef.type) {
			case SisType.Date:
				// noinspection EqualityComparisonWithCoercionJS
				if (val == 0) return true; // special value always null representation
		}
		// no strict match needed for nullValue
		// noinspection EqualityComparisonWithCoercionJS
		return val === null || val === undefined || val === '' || _.isNaN(val) || val == cfDef.nullValue;
	},

	setInitValue(cfDef: CustomFieldTO, model: Record<string, CFValue>) {
		if (CF.isCFNamespace(cfDef)) {
			if (cfDef.minAmount < 1 || cfDef.defaultValue === null) return;
			const dstField = CF.getCFNamespaceField(cfDef);
			const currentCFMembers = _.filter(getter(model, 'cf'), cfMember => cfMember.dstField === dstField);
			let initAmount = cfDef.minAmount - _.size(currentCFMembers);
			let pos = Math.max(-1, ..._.compact(currentCFMembers.map(x => x.pos))) + 1;
			while (initAmount > 0) {
				CF.setValueTo(cfDef, model, {
					pos,
					dstField,
					data: cfDef.defaultValue,
				});
				pos++;
				initAmount--;
			}
		} else {
			if (cfDef.defaultValue !== null) {
				CF.setValueTo(cfDef, model, cfDef.defaultValue);
			} else {
				CF.setValueTo(cfDef, model, null);
			}
		}
	},

	applyPrimitiveType(cfDef: CustomFieldTO, val: CFValue) {
		if (val === null || val === undefined || _.isNaN(val)) {
			return null;
		}
		val = castToSisTypePrimitive(val, cfDef.type);
		if (val === '' || _.isNaN(val)) val = null;
		return val;
	},

	parseValue(cfDef: CustomFieldTO, val: CFValue, options?: SisTimeOptionsType | SisDurationOptionsType) {
		// no strict comparison because nullValue and defaultValue are always Strings, but val can be anything
		// noinspection EqualityComparisonWithCoercionJS
		if (CF.isNull(cfDef, val)) {
			return null;
		}
		switch (cfDef.type) {
			case SisType.Key:
				val = applySisKeyFieldRuleset(val, cfDef.length);
				break;
			case SisType.Int:
			case SisType.Number:
				if (val && typeof val !== 'number') val = parseInt(val);
				break;
			case SisType.Float:
				if (val && typeof val !== 'number') val = parseFloat(val);
				break;
			case SisType.Date:
				if (val) val = getSisDateAsNumber(val);
				break;
			case SisType.Time:
				if (val) val = getSisTimeAsNumber(val, options);
				break;
			case SisType.Duration:
				if (val) val = getSisDurationAsNumber(val, options);
				break;
			case SisType.DurationA:
				if (options && options.userInput) {
					let cfAttr = CF.getCFAttrFromCFAttrs(cfDef, 'durationFormat')?.value || '1';
					if (val) val = SisDuration.toModel(val, parseInt(cfAttr));
				} else {
					if (val) val = getSisDurationAsNumber(val, options);
				}
				break;
		}

		if (val === '' || _.isNaN(val)) return null;
		return val;
	},

	transformForView(cfDef: CustomFieldTO, val: CFValue) {
		if (val == null) return val;
		switch (cfDef.type) {
			case SisType.Key:
				let s = val + ''
				const hasValue = s.trim().length
				if (/^[0-9]*$/g.test(s)) s = s.replace(/^0+/, '');
				if (hasValue && !s.length) s = '0' // '000000' is a valid value -> show '0' in view instead of ''
				val = s.toUpperCase();
				break;
			case SisType.Date:
				val = getSisDateAsISO(val);
				break;
			case SisType.Time:
				val = getSisTimeAsISO(val);
				break;
			case SisType.Duration:
				val = getSisDurationAsISO(val);
				break;
			case SisType.DurationA:
				let cfAttr = CF.getCFAttrFromCFAttrs(cfDef, 'durationFormat')?.value || '1';
				val = SisDuration.toView(val, parseInt(cfAttr));
				break;
		}
		return val;
	},

	getDqRelevantFieldNames(cfDef: CustomFieldTO) {
		return [cfDef.dstField].concat(cfDef.dqParams.map(f => f.srcName))
	},

	getInputAttributes(cfDef: CustomFieldTO) {
		// todo remove and create/use view param table (key value store mapped to cfdef)
		if (typeof cfDef.input !== 'string') return {};
		let idx = cfDef.input.lastIndexOf("?");
		if (idx < 0) return {};
		let str = cfDef.input.substr(idx + 1);
		return _.transform(str.split('&'), (result, p) => {
			const arr = p.split('=');
			if (arr[0] && arr[1]) {
				result[arr[0]] = arr[1];
			}
		}, {} as Record<string, string>);
	},
};

export const CFMemberComparator = (a: { pos: number }, b: { pos: number }) => a.pos - b.pos;

window.sis = {
	...window.sis,
	CF,
};
